// Paket A8b — E-Mail-Werbung nur mit nachweisbarer Einwilligung (Double-Opt-in).
//
// Server-Vertrag (server/email-marketing-consent.js):
//   GET  /api/email-marketing/status
//        → { status: 'off'|'pending'|'confirmed', can_subscribe, reason,
//            resend_available_at, consent_source, consented_at }
//   POST /api/email-marketing/subscribe { source: 'settings'|'in_app_prompt' }
//        → Status + { sent, reason? } — schickt die Bestätigungsmail; setzt KEINE
//          Einwilligung. Die entsteht erst durch den Klick auf den Link in der Mail.
//   POST /api/email-marketing/withdraw → Status 'off' (sofort wirksam)
//   GET  /api/email-marketing/prompt → { prompt: { eligible: true } | null, reason, final }
//        final = endgültiger Grund (Sitzung): der Client fragt nicht erneut nach
//   POST /api/email-marketing/prompt/claim → { ok, claimed }  (genau einmal je Konto)
//   POST /api/email-marketing/prompt/answer { answer: 'yes'|'no'|'dismiss' }
//
// Der Client vertraut den Flags des Servers; fehlende oder fremde Felder ergeben die
// vorsichtige Lesart (aus, nicht einschaltbar, nicht berechtigt).
import { apiRequest } from './client';

export type EmailMarketingStatus = 'off' | 'pending' | 'confirmed';
/**
 * Warum ein Konto die E-Mails (noch) nicht einschalten kann. `birthdate_missing`: das Alter
 * ist unbekannt (zählt nicht als „alt genug", Art. 8 DSGVO); `language_unsupported`: die
 * Einwilligungstexte liegen in der Sprache des Kontos noch nicht übersetzt vor.
 */
export type EmailMarketingBlockReason =
  | 'guest'
  | 'no_email'
  | 'age_restricted'
  | 'birthdate_missing'
  | 'language_unsupported'
  | 'not_found'
  | null;
export type EmailMarketingSource = 'settings' | 'in_app_prompt';
export type EmailMarketingPromptAnswer = 'yes' | 'no' | 'dismiss';

export interface EmailMarketingState {
  status: EmailMarketingStatus;
  canSubscribe: boolean;
  reason: EmailMarketingBlockReason;
  /** Ab wann eine neue Bestätigungsmail geschickt werden darf; null = jetzt (oder nichts ausstehend). */
  resendAvailableAt: string | null;
  consentSource: string | null;
}

export interface EmailMarketingSubscribeResult extends EmailMarketingState {
  /** true = die Bestätigungsmail ist soeben rausgegangen. */
  sent: boolean;
  /** Grund, falls nichts gesendet wurde (z. B. 'throttled', 'no_email', 'send_failed'). */
  failureReason: string | null;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

const STATUSES: readonly EmailMarketingStatus[] = ['off', 'pending', 'confirmed'];
const BLOCK_REASONS: readonly string[] = ['guest', 'no_email', 'age_restricted', 'birthdate_missing', 'language_unsupported', 'not_found'];

export function normalizeEmailMarketingState(value: unknown): EmailMarketingState {
  const raw = record(value);
  const status = STATUSES.find((candidate) => candidate === raw.status) ?? 'off';
  const reason = typeof raw.reason === 'string' && BLOCK_REASONS.includes(raw.reason)
    ? raw.reason as EmailMarketingBlockReason
    : null;
  return {
    status,
    // Nur ein ausdrückliches true schaltet den Schalter frei.
    canSubscribe: raw.can_subscribe === true,
    reason,
    resendAvailableAt: typeof raw.resend_available_at === 'string' ? raw.resend_available_at : null,
    consentSource: typeof raw.consent_source === 'string' ? raw.consent_source : null,
  };
}

export function normalizeEmailMarketingSubscribeResult(value: unknown): EmailMarketingSubscribeResult {
  const raw = record(value);
  const state = normalizeEmailMarketingState(value);
  return {
    ...state,
    sent: raw.sent === true,
    // Der Sperrgrund der Anlage (guest/no_email/age_restricted) steckt in `reason`
    // und wird vom Zustand schon gelesen; die übrigen Gründe landen hier.
    failureReason: typeof raw.reason === 'string' ? raw.reason : null,
  };
}

export async function getEmailMarketingStatus(): Promise<EmailMarketingState> {
  return normalizeEmailMarketingState(await apiRequest<unknown>('/api/email-marketing/status'));
}

export async function subscribeEmailMarketing(source: EmailMarketingSource = 'settings'): Promise<EmailMarketingSubscribeResult> {
  return normalizeEmailMarketingSubscribeResult(await apiRequest<unknown>('/api/email-marketing/subscribe', {
    method: 'POST',
    body: { source },
  }));
}

export async function withdrawEmailMarketing(): Promise<EmailMarketingState> {
  return normalizeEmailMarketingState(await apiRequest<unknown>('/api/email-marketing/withdraw', { method: 'POST' }));
}

export interface EmailMarketingPromptState {
  /** true = dem Konto darf die einmalige Frage gezeigt werden (Zeitpunkt entscheidet der Client). */
  eligible: boolean;
  /** true = endgültiger Grund (z. B. schon gefragt, zu jung, bereits bestätigt): nicht erneut nachfragen. */
  final: boolean;
}

export async function getEmailMarketingPromptState(): Promise<EmailMarketingPromptState> {
  const raw = record(await apiRequest<unknown>('/api/email-marketing/prompt'));
  const eligible = record(raw.prompt).eligible === true;
  // Nur ein ausdrückliches final: true beendet das Nachfragen; im Zweifel weiter sparsam fragen.
  return { eligible, final: !eligible && raw.final === true };
}

/** true = genau dieser Aufruf darf die Frage jetzt anzeigen (höchstens einmal je Konto). */
export async function claimEmailMarketingPrompt(): Promise<boolean> {
  const raw = record(await apiRequest<unknown>('/api/email-marketing/prompt/claim', { method: 'POST' }));
  return raw.claimed === true;
}

export async function answerEmailMarketingPrompt(answer: EmailMarketingPromptAnswer): Promise<EmailMarketingSubscribeResult> {
  return normalizeEmailMarketingSubscribeResult(await apiRequest<unknown>('/api/email-marketing/prompt/answer', {
    method: 'POST',
    body: { answer },
  }));
}
